import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Shield, LogOut, RefreshCw, ExternalLink, Pencil,
  Trash2, Ban, CheckCircle, X, Eye, EyeOff, Building2, Activity, Pause,
  Bus, Mountain, DollarSign, FileText, MessageCircle, Lock, Check,
  Sparkles, SlidersHorizontal, Layers,
} from 'lucide-react';
import api from '../api';
import { useSuperAdmin } from '../contexts/SuperAdminContext';
import BrandingPreview from '../components/BrandingPreview';
import ColorPicker from '../components/ColorPicker';
import {
  MODULE_REGISTRY, TEMPLATES, TEMPLATE_LABELS, detectTemplate,
} from '../config/modules';

const EMPTY = {
  slug: '', nomeEmpresa: '', nomeDoSistema: '',
  corPrimaria: '#0147a0', corSecundaria: '#d72d21', corSidebar: '#001e45',
  logo: '', imagemLogin: '', adminEmail: '', adminSenha: '',
  modulos: [...TEMPLATES.transporte],
};

const TPL_ICONS = { transporte: Bus, aventura: Mountain, completo: Sparkles, custom: SlidersHorizontal };
const CAT_ICONS = { Bus, Mountain, DollarSign, FileText, MessageCircle };

function ModuleSelector({ value, onChange }) {
  const enabled = new Set(value);
  const activeTpl = detectTemplate(value);

  const applyTemplate = (name) => {
    if (name === 'custom') return;
    onChange([...TEMPLATES[name]]);
  };
  const toggle = (key) => {
    const next = new Set(enabled);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };

  return (
    <div className="border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Módulos do Sistema</p>
        <span className="text-xs font-semibold text-slate-500">{value.length} ativo(s)</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {['transporte', 'aventura', 'completo', 'custom'].map(name => {
          const Icon = TPL_ICONS[name];
          const active = activeTpl === name;
          return (
            <button key={name} type="button" onClick={() => applyTemplate(name)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-colors ${
                active ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
              <Icon size={16} className={active ? 'text-blue-600' : 'text-slate-400'} />
              <span className={`text-xs font-semibold ${active ? 'text-blue-700' : 'text-slate-600'}`}>{TEMPLATE_LABELS[name]}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {MODULE_REGISTRY.map(cat => {
          const CatIcon = CAT_ICONS[cat.icon] || Layers;
          return (
            <div key={cat.category} className="border border-slate-100 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                <CatIcon size={13} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600">{cat.category}</span>
              </div>
              <div>
                {cat.modules.map(m => {
                  const soon = m.status === 'soon';
                  const on = enabled.has(m.key);
                  return (
                    <button key={m.key} type="button" disabled={soon}
                      onClick={() => !soon && toggle(m.key)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 border-b border-slate-50 last:border-0 text-left transition-colors ${
                        soon ? 'cursor-not-allowed opacity-60' : 'hover:bg-slate-50'}`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                        on ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
                        {on && <Check size={11} className="text-white" />}
                      </span>
                      <span className="text-xs text-slate-700 flex-1">{m.label}</span>
                      {m.tag && !soon && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{m.tag}</span>}
                      {soon && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 flex items-center gap-1"><Lock size={9} />Em breve</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400";

function TenantModal({ open, onClose, tenant, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const fileRef = useRef();
  const loginRef = useRef();
  const isNew = !tenant;

  useEffect(() => {
    if (!open) return;
    if (isNew) { setForm(EMPTY); return; }
    setLoadingFull(true);
    api.get(`/superadmin/tenants/${tenant.slug}`)
      .then(res => setForm({ ...EMPTY, ...res.data, adminEmail: '', adminSenha: '' }))
      .catch(() => setForm({ ...EMPTY, ...tenant, adminEmail: '', adminSenha: '' }))
      .finally(() => setLoadingFull(false));
  }, [open, tenant, isNew]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const readFile = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800_000) { toast.error('Imagem muito grande (máx 800KB)'); return; }
    const reader = new FileReader();
    reader.onload = () => set(field, reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isNew) {
        await api.post('/superadmin/tenants', form);
        toast.success('Agência criada!');
      } else {
        const { nomeEmpresa, nomeDoSistema, corPrimaria, corSecundaria, corSidebar, logo, imagemLogin, modulos } = form;
        await api.put(`/superadmin/tenants/${tenant.slug}`, { nomeEmpresa, nomeDoSistema, corPrimaria, corSecundaria, corSidebar, logo, imagemLogin, modulos });
        toast.success('Branding atualizado!');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-5xl flex flex-col"
        style={{ maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}>
        <div className="h-1.5 rounded-t-2xl bg-slate-900" />
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">{isNew ? 'Nova Agência' : `Editar: ${tenant.nomeEmpresa}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        {loadingFull ? (
          <div className="flex-1 flex items-center justify-center py-20 text-slate-400 text-sm">Carregando dados...</div>
        ) : (
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1 min-h-0">
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left — form */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 border-r border-slate-100">
              <div className="grid grid-cols-2 gap-4">
                {isNew && (
                  <Field label="Slug (URL) *">
                    <input className={inp} value={form.slug}
                      onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="agencia-x" required />
                  </Field>
                )}
                <Field label="Nome da Empresa *">
                  <input className={inp} value={form.nomeEmpresa} onChange={e => set('nomeEmpresa', e.target.value)} required />
                </Field>
                <Field label="Nome do Sistema *">
                  <input className={inp} value={form.nomeDoSistema} onChange={e => set('nomeDoSistema', e.target.value)}
                    placeholder="Ex: Turismo ABC" required />
                </Field>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Identidade Visual</p>
                <div className="space-y-5">
                  <ColorPicker label="Cor Primária (botões, cards, links)"
                    value={form.corPrimaria} onChange={v => set('corPrimaria', v)} />
                  <ColorPicker label="Cor Secundária (avatares, badges)"
                    value={form.corSecundaria} onChange={v => set('corSecundaria', v)} />
                  <ColorPicker label="Cor do Sidebar (menu lateral)"
                    value={form.corSidebar} onChange={v => set('corSidebar', v)} />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Imagens</p>
                <Field label="Logo da agência (PNG/JPG – máx 800KB)">
                  <div className="flex items-center gap-3">
                    {form.logo && (
                      <img src={form.logo} alt="logo" className="w-12 h-12 rounded-xl border border-slate-200 object-cover" />
                    )}
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
                      {form.logo ? 'Alterar logo' : 'Fazer upload'}
                    </button>
                    {form.logo && (
                      <button type="button" onClick={() => set('logo', '')}
                        className="text-xs text-red-500 hover:underline">Remover</button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => readFile(e, 'logo')} />
                  </div>
                </Field>
                <Field label="Imagem de Login (opcional)">
                  <div className="flex items-center gap-3">
                    {form.imagemLogin && (
                      <img src={form.imagemLogin} alt="login" className="w-12 h-12 rounded-xl border border-slate-200 object-cover" />
                    )}
                    <button type="button" onClick={() => loginRef.current?.click()}
                      className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
                      {form.imagemLogin ? 'Alterar imagem' : 'Fazer upload'}
                    </button>
                    {form.imagemLogin && (
                      <button type="button" onClick={() => set('imagemLogin', '')}
                        className="text-xs text-red-500 hover:underline">Remover</button>
                    )}
                    <input ref={loginRef} type="file" accept="image/*" className="hidden" onChange={e => readFile(e, 'imagemLogin')} />
                  </div>
                </Field>
              </div>

              <ModuleSelector value={form.modulos || []} onChange={v => set('modulos', v)} />

              {isNew && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Admin da Agência</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="E-mail do Admin *">
                      <input type="email" className={inp} value={form.adminEmail}
                        onChange={e => set('adminEmail', e.target.value)} required />
                    </Field>
                    <Field label="Senha do Admin *">
                      <input type="password" className={inp} value={form.adminSenha}
                        onChange={e => set('adminSenha', e.target.value)} required minLength={6} />
                    </Field>
                  </div>
                </div>
              )}
            </div>

            {/* Right — live preview */}
            <div className="w-80 flex-shrink-0 p-6 overflow-y-auto bg-slate-50/50">
              <BrandingPreview
                nomeDoSistema={form.nomeDoSistema}
                nomeEmpresa={form.nomeEmpresa}
                logo={form.logo}
                corPrimaria={form.corPrimaria}
                corSecundaria={form.corSecundaria}
                corSidebar={form.corSidebar}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-slate-100 bg-white rounded-b-2xl">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancelar</button>
            <button type="submit" disabled={submitting}
              className="px-5 py-2 text-sm bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-60">
              {submitting ? 'Salvando...' : isNew ? 'Criar Agência' : 'Salvar alterações'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

function AdminModal({ open, onClose, tenant, onSuccess }) {
  const [form, setForm] = useState({ email: '', senha: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && tenant) setForm({ email: tenant.adminEmail || '', senha: '' });
  }, [open, tenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/superadmin/tenants/${tenant.slug}/admin`, form);
      toast.success('Admin atualizado');
      onSuccess();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar'); }
    finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-bold text-slate-800">Admin: {tenant?.nomeEmpresa}</h2>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Field label="E-mail do Admin">
            <input type="email" className={inp} value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="Nova Senha (deixe vazio para não alterar)">
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} className={inp + ' pr-10'} value={form.senha}
                onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} minLength={form.senha ? 6 : 0} />
              <button type="button" onClick={() => setShowPwd(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancelar</button>
            <button type="submit" disabled={submitting} className="px-5 py-2 text-sm bg-slate-900 text-white rounded-xl disabled:opacity-60">
              {submitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, logout, saUser } = useSuperAdmin();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantModal, setTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [adminModal, setAdminModal] = useState(false);
  const [adminTenant, setAdminTenant] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) navigate('/superadmin/login', { replace: true });
  }, [isAuthenticated, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/tenants');
      setTenants(res.data);
    } catch { toast.error('Erro ao carregar agências'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (t) => {
    const newStatus = t.status === 'ativo' ? 'suspenso' : 'ativo';
    try {
      await api.patch(`/superadmin/tenants/${t.slug}/status`, { status: newStatus });
      toast.success(`Agência ${newStatus === 'ativo' ? 'reativada' : 'suspensa'}`);
      load();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Excluir permanentemente "${t.nomeEmpresa}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${t.slug}`);
      toast.success('Agência excluída');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const openCreate = () => { setEditingTenant(null); setTenantModal(true); };
  const openEdit = (t) => { setEditingTenant(t); setTenantModal(true); };
  const openAdmin = (t) => { setAdminTenant(t); setAdminModal(true); };

  const active = tenants.filter(t => t.status === 'ativo').length;
  const suspended = tenants.filter(t => t.status === 'suspenso').length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield size={18} />
            </div>
            <div>
              <p className="font-bold text-sm">Sistema Tour · Painel Master</p>
              <p className="text-xs text-slate-400 hidden sm:block">Gestão de agências e branding</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm hidden sm:inline">{saUser?.username}</span>
            <button onClick={logout}
              className="flex items-center gap-1.5 text-slate-300 hover:text-red-300 hover:bg-red-500/10 px-3 py-1.5 rounded-xl transition-colors text-sm">
              <LogOut size={15} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { label: 'Total de Agências', value: tenants.length, icon: Building2, color: '#0147a0' },
            { label: 'Ativas', value: active, icon: Activity, color: '#339933' },
            { label: 'Suspensas', value: suspended, icon: Pause, color: '#d72d21' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{c.label}</p>
                  <p className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}18` }}>
                  <c.icon size={18} style={{ color: c.color }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Table header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-800 text-lg">Agências Cadastradas</h2>
            <p className="text-slate-500 text-sm">Cada agência tem banco de dados isolado</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load}
              className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 px-3 py-2 rounded-xl hover:bg-slate-50">
              <RefreshCw size={14} /> Atualizar
            </button>
            <button onClick={openCreate}
              className="flex items-center gap-1.5 text-sm bg-slate-900 text-white px-4 py-2 rounded-xl hover:bg-slate-800">
              <Plus size={14} /> Nova Agência
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Agência', 'Slug', 'Admin', 'Status', 'Criado em', 'Ações'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  Array(3).fill(0).map((_, i) => (
                    <tr key={i}>{Array(6).fill(0).map((_, j) => (
                      <td key={j} className="px-5 py-4"><div className="h-3 bg-slate-100 rounded animate-pulse" /></td>
                    ))}</tr>
                  ))
                ) : tenants.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">
                    Nenhuma agência cadastrada. Clique em "Nova Agência" para começar.
                  </td></tr>
                ) : tenants.map(t => (
                  <tr key={t.slug} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 min-w-[180px]">
                        {t.logo ? (
                          <img src={t.logo} alt="" className="w-9 h-9 rounded-xl object-cover border border-slate-200 flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                            style={{ background: t.corPrimaria || '#0147a0' }}>
                            {(t.nomeEmpresa || t.slug)[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-700 leading-tight">{t.nomeEmpresa}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{t.nomeDoSistema}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded-lg">{t.slug}</code>
                    </td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{t.adminEmail}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        t.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'ativo' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {t.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-400 text-xs">{t.dataCriacao?.slice(0, 10)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <a href={`/${t.slug}/login`} target="_blank" rel="noopener noreferrer"
                          title="Abrir sistema"
                          className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <ExternalLink size={14} />
                        </a>
                        <button onClick={() => openEdit(t)} title="Editar branding"
                          className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => openAdmin(t)} title="Editar admin"
                          className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                          <Shield size={14} />
                        </button>
                        <button onClick={() => handleStatus(t)}
                          title={t.status === 'ativo' ? 'Suspender' : 'Reativar'}
                          className={`p-2 rounded-lg transition-colors ${t.status === 'ativo' ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'}`}>
                          {t.status === 'ativo' ? <Ban size={14} /> : <CheckCircle size={14} />}
                        </button>
                        <button onClick={() => handleDelete(t)} title="Excluir"
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <TenantModal
        open={tenantModal}
        onClose={() => setTenantModal(false)}
        tenant={editingTenant}
        onSuccess={load}
      />
      <AdminModal
        open={adminModal}
        onClose={() => setAdminModal(false)}
        tenant={adminTenant}
        onSuccess={load}
      />
    </div>
  );
}
